import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../../../shared/api/apiClient";
import "./TaskPhotosGallery.css";
import { useTranslation } from "react-i18next";

const asArray = (value) => (Array.isArray(value) ? value : []);

const getErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail ?? error?.message ?? error;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((x) => x?.msg || JSON.stringify(x)).join("; ");
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return String(detail);
};

const formatBytes = (size) => {
  const bytes = Number(size || 0);
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const IMAGE_EXTENSIONS = new Set([
  "jpg", "jpeg", "png", "webp", "gif", "bmp", "svg", "heic", "heif", "avif", "tif", "tiff",
]);

const getFileExtension = (file) => {
  const name = String(file?.name || "").toLowerCase();
  const parts = name.split(".");
  return parts.length > 1 ? parts.pop() : "";
};

const isAcceptedImageFile = (file) => {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  if (type.startsWith("image/")) return true;
  return IMAGE_EXTENSIONS.has(getFileExtension(file));
};

const isImage = (photo) => String(photo?.mime_type || "").toLowerCase().startsWith("image/");
const getDisplayName = (photo, index, fallbackLabel) => photo?.file_name || `${fallbackLabel} ${index + 1}`;

const PHOTO_WORKFLOW_GROUPS = [
  { key: "before", label: "task_photos_group_before", hint: "task_photos_group_before_hint" },
  { key: "during", label: "task_photos_group_during", hint: "task_photos_group_during_hint" },
  { key: "after", label: "task_photos_group_after", hint: "task_photos_group_after_hint" },
];

const getPhotoWorkflowGroup = (photo) => {
  const name = String(photo?.file_name || "").toLowerCase();
  if (/^(before)__/.test(name) || name.includes("до работы")) return "before";
  if (/^(after)__/.test(name) || name.includes("после работы")) return "after";
  return "during";
};

const stripPhotoWorkflowPrefix = (value) =>
  String(value || "").replace(/^((before|during|after)__)+/i, "");

const makeUploadItem = (file, index, fallbackLabel) => ({
  id: `${Date.now()}-${index}-${Math.random().toString(16).slice(2)}`,
  name: file?.name || `${fallbackLabel} ${index + 1}`,
  size: file?.size || 0,
  progress: 0,
  status: "queued",
  error: "",
});

const makeDeleteItem = (photo, index, fallbackLabel) => ({
  id: photo?.id ?? `${Date.now()}-${index}`,
  name: photo?.file_name || `${fallbackLabel} ${index + 1}`,
  size: photo?.size_bytes || 0,
  progress: 100,
  status: "queued",
  error: "",
});

const IconButton = ({ children, className = "", ...props }) => (
  <button type="button" className={`taskPhotosIconButton ${className}`.trim()} {...props}>
    {children}
  </button>
);

const TaskPhotosGallery = ({
  entityType,
  entityId,
  uploadUrl,
  title,
  hint,
  addLabel,
  onChanged,
  readonly = false,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const dropDepthRef = useRef(0);
  const touchStartRef = useRef(null);

  const [photos, setPhotos] = useState([]);
  const [previewUrls, setPreviewUrls] = useState({});
  const [downloadingId, setDownloadingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [deleteQueue, setDeleteQueue] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [error, setError] = useState("");
  const [viewerIndex, setViewerIndex] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [workspaceLayout, setWorkspaceLayout] = useState("gallery");
  const [uploadCategory, setUploadCategory] = useState("during");
  const displayTitle = title || t("task_photos_title");
  const displayHint = hint || t("task_photos_saved_drive");
  const displayAddLabel = addLabel || t("add_photos");
  const photoFallbackLabel = t("task_photos_photo");

  const hasEntity = Boolean(entityType && entityId !== undefined && entityId !== null && entityId !== "");
  const currentPhoto = viewerIndex === null ? null : photos[viewerIndex] || null;
  const showEmptyState = !uploading && !uploadQueue.length && !loading && !photos.length;
  const selectedCount = selectedIds.size;
  const selectedPhotos = useMemo(() => photos.filter((photo) => selectedIds.has(photo.id)), [photos, selectedIds]);

  const totalUploadProgress = useMemo(() => {
    if (!uploadQueue.length) return 0;
    const sum = uploadQueue.reduce((acc, item) => acc + Number(item.progress || 0), 0);
    return Math.max(0, Math.min(100, Math.round(sum / uploadQueue.length)));
  }, [uploadQueue]);

  const totalDeleteProgress = useMemo(() => {
    if (!deleteQueue.length) return 0;
    const sum = deleteQueue.reduce((acc, item) => acc + Number(item.progress || 0), 0);
    return Math.max(0, Math.min(100, Math.round(sum / deleteQueue.length)));
  }, [deleteQueue]);

  const photosMetaLabel = useMemo(() => {
    if (uploading) return t("task_photos_uploading_progress", { progress: totalUploadProgress });
    if (loading) return t("task_photos_loading");
    if (!photos.length) return displayHint;
    const count = photos.length;
    return t("task_photos_saved_count", { count });
  }, [displayHint, loading, photos.length, t, totalUploadProgress, uploading]);

  const revokePreviewUrls = useCallback((urls) => {
    Object.values(urls || {}).forEach((url) => {
      if (typeof url === "string" && url.startsWith("blob:")) URL.revokeObjectURL(url);
    });
  }, []);

  const loadPhotos = useCallback(async () => {
    if (!hasEntity) {
      setPhotos([]);
      setPreviewUrls((prev) => {
        revokePreviewUrls(prev);
        return {};
      });
      return;
    }

    try {
      setLoading(true);
      setError("");
      const { data } = await api.get("/auth/storage/files", {
        params: { entity_type: entityType, entity_id: entityId },
      });
      const loadedPhotos = asArray(data).filter(isImage);

      // <img src="/api/..."> cannot attach the Bearer token used by apiClient.
      // Load the image bytes through the authenticated client and render safe blob URLs instead.
      const hydrated = await Promise.all(
        loadedPhotos.map(async (photo) => {
          try {
            const response = await api.get(`/auth/storage/files/${photo.id}/download`, { responseType: "blob" });
            return [photo.id, URL.createObjectURL(response.data)];
          } catch (previewError) {
            console.error("Failed to load task photo preview", photo?.id, previewError);
            return [photo.id, ""];
          }
        })
      );

      const nextPreviewUrls = Object.fromEntries(hydrated);
      setPreviewUrls((prev) => {
        revokePreviewUrls(prev);
        return nextPreviewUrls;
      });
      setPhotos(loadedPhotos);
      setSelectedIds((prev) => new Set(loadedPhotos.filter((photo) => prev.has(photo.id)).map((photo) => photo.id)));
      setImageErrors({});
    } catch (err) {
      console.error("Failed to load photos", err);
      setPhotos([]);
      setPreviewUrls((prev) => {
        revokePreviewUrls(prev);
        return {};
      });
      setError(getErrorMessage(err, t("task_photos_load_failed")));
    } finally {
      setLoading(false);
    }
  }, [entityId, entityType, hasEntity, revokePreviewUrls, t]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  useEffect(() => () => {
    revokePreviewUrls(previewUrls);
  }, [previewUrls, revokePreviewUrls]);

  const downloadPhoto = useCallback(async (photo) => {
    if (!photo?.id || downloadingId) return;
    try {
      setDownloadingId(photo.id);
      setError("");
      const response = await api.get(`/auth/storage/files/${photo.id}/download`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = stripPhotoWorkflowPrefix(photo.file_name || "photo");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error("Failed to download photo", err);
      setError(getErrorMessage(err, t("task_photos_preview_failed")));
    } finally {
      setDownloadingId(null);
    }
  }, [downloadingId, t]);

  useEffect(() => {
    let mounted = true;

    api
      .get("/auth/crmSettings")
      .then(({ data }) => {
        if (!mounted) return;
        const value = data?.settings_json?.files?.workspace_layout;
        setWorkspaceLayout(value === "workflow" ? "workflow" : "gallery");
      })
      .catch((err) => {
        console.error("Failed to load files layout setting", err);
        if (mounted) setWorkspaceLayout("gallery");
      });

    return () => {
      mounted = false;
    };
  }, []);

  const emitChanged = useCallback(async () => {
    if (typeof onChanged === "function") await onChanged();
  }, [onChanged]);

  const uploadOneFile = async (file, queueId, category = "during") => {
    const formData = new FormData();

    // Main storage endpoint expects `file`. Old task-specific endpoint expects `files`.
    // Prefer the stable storage endpoint because it saves entity_type="task" and entity_id=task.id,
    // so the same gallery can immediately load the uploaded photos back.
    const originalName = file.name || "photo";
    const cleanName = stripPhotoWorkflowPrefix(originalName);
    const storedName =
      workspaceLayout === "workflow"
        ? `${category}__${cleanName}`
        : cleanName;

    formData.append("file", file, storedName);

    await api.post("/auth/storage/files", formData, {
      params: { entity_type: entityType, entity_id: entityId },
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (progressEvent) => {
        const loaded = Number(progressEvent.loaded || 0);
        const total = Number(progressEvent.total || file.size || 0);
        const percent = total ? Math.min(99, Math.round((loaded / total) * 100)) : 50;
        setUploadQueue((prev) => prev.map((item) => (
          item.id === queueId ? { ...item, status: "uploading", progress: percent } : item
        )));
      },
    });

    setUploadQueue((prev) => prev.map((item) => (
      item.id === queueId ? { ...item, status: "done", progress: 100 } : item
    )));
  };

  const uploadFiles = async (fileList, category = uploadCategory) => {
    const selected = Array.from(fileList || []);

    if (!hasEntity || readonly) {
      setError(t("task_photos_no_task"));
      return;
    }

    if (!selected.length) return;

    const files = selected.filter(isAcceptedImageFile);
    const rejected = selected.filter((file) => !isAcceptedImageFile(file));

    if (!files.length) {
      setError(
        rejected.length
          ? t("task_photos_not_image", { files: rejected.map((file) => file.name || t("task_photos_unknown_file")).join(", ") })
          : t("task_photos_no_images_selected")
      );
      return;
    }

    const queue = files.map((file, index) => makeUploadItem(file, index, photoFallbackLabel));
    setUploadQueue(queue);
    setUploading(true);
    setError(rejected.length ? t("task_photos_skipped_non_images", { files: rejected.map((file) => file.name || t("task_photos_unknown_file")).join(", ") }) : "");

    let failed = 0;

    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      const queueId = queue[index].id;
      setUploadQueue((prev) => prev.map((item) => (
        item.id === queueId ? { ...item, status: "uploading", progress: Math.max(item.progress, 1) } : item
      )));

      try {
        await uploadOneFile(file, queueId, category);
      } catch (err) {
        failed += 1;
        console.error("Failed to upload photo", file?.name, err);
        const message = getErrorMessage(err, t("task_photos_upload_failed"));
        setUploadQueue((prev) => prev.map((item) => (
          item.id === queueId ? { ...item, status: "failed", error: message, progress: 100 } : item
        )));
      }
    }

    await loadPhotos();
    await emitChanged();

    if (failed) {
      setError(
        t(
          failed === 1
            ? "task_photos_upload_failed_one"
            : "task_photos_upload_failed_other",
          { count: failed },
        ),
      );
    }

    setUploading(false);
    window.setTimeout(() => {
      setUploadQueue((prev) => (prev.some((item) => item.status === "failed") ? prev : []));
    }, 1400);
  };

  const handleInputChange = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    await uploadFiles(selected);
  };

  const toggleSelected = useCallback((photoId) => {
    if (!photoId || readonly) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(photoId)) next.delete(photoId);
      else next.add(photoId);
      return next;
    });
  }, [readonly]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const animateDeleteProgress = (queueId) => {
    setDeleteQueue((prev) => prev.map((item) => (
      item.id === queueId ? { ...item, status: "deleting", progress: 100 } : item
    )));

    return window.setInterval(() => {
      setDeleteQueue((prev) => prev.map((item) => {
        if (item.id !== queueId || item.status !== "deleting") return item;
        return { ...item, progress: Math.max(12, Number(item.progress || 100) - 7) };
      }));
    }, 90);
  };

  const finishDeleteProgress = (queueId, status = "done", errorMessage = "") => {
    setDeleteQueue((prev) => prev.map((item) => (
      item.id === queueId
        ? { ...item, status, error: errorMessage, progress: status === "done" ? 0 : 100 }
        : item
    )));
  };

  const deleteSelectedPhotos = async () => {
    if (readonly || bulkDeleting || !selectedPhotos.length) return;
    const count = selectedPhotos.length;
    const confirmed = window.confirm(
      t(
        count === 1
          ? "task_photos_delete_selected_one"
          : "task_photos_delete_selected_other",
        { count },
      ),
    );
    if (!confirmed) return;

    const queue = selectedPhotos.map((photo, index) => makeDeleteItem(photo, index, photoFallbackLabel));
    setDeleteQueue(queue);

    try {
      setBulkDeleting(true);
      setError("");
      const deletedIds = [];

      for (const photo of selectedPhotos) {
        const queueId = photo.id;
        setDeletingId(photo.id);
        const timer = animateDeleteProgress(queueId);
        try {
          await api.delete(`/auth/storage/files/${photo.id}`);
          deletedIds.push(photo.id);
          window.clearInterval(timer);
          finishDeleteProgress(queueId, "done");
        } catch (err) {
          window.clearInterval(timer);
          finishDeleteProgress(queueId, "failed", getErrorMessage(err, t("failed")));
          throw err;
        }
      }

      setPhotos((prev) => prev.filter((item) => !deletedIds.includes(item.id)));
      setSelectedIds(new Set());
      setViewerIndex((prev) => {
        if (prev === null) return null;
        const remaining = photos.filter((item) => !deletedIds.includes(item.id)).length;
        if (remaining <= 0) return null;
        return Math.min(prev, remaining - 1);
      });
      await emitChanged();
      window.setTimeout(() => setDeleteQueue([]), 900);
    } catch (err) {
      console.error("Failed to delete selected photos", err);
      setError(getErrorMessage(err, t("task_photos_delete_selected_failed")));
      await loadPhotos();
    } finally {
      setDeletingId(null);
      setBulkDeleting(false);
    }
  };

  const deletePhoto = async (photo) => {
    if (!photo?.id || readonly) return;
    const confirmed = window.confirm(t("task_photos_delete_confirm"));
    if (!confirmed) return;

    const queue = [makeDeleteItem(photo, 0, photoFallbackLabel)];
    setDeleteQueue(queue);
    const timer = animateDeleteProgress(photo.id);

    try {
      setDeletingId(photo.id);
      setError("");
      await api.delete(`/auth/storage/files/${photo.id}`);
      window.clearInterval(timer);
      finishDeleteProgress(photo.id, "done");
      setPhotos((prev) => prev.filter((item) => item.id !== photo.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(photo.id);
        return next;
      });
      setViewerIndex((prev) => {
        if (prev === null) return null;
        const remaining = photos.length - 1;
        if (remaining <= 0) return null;
        return Math.min(prev, remaining - 1);
      });
      await emitChanged();
      window.setTimeout(() => setDeleteQueue([]), 900);
    } catch (err) {
      window.clearInterval(timer);
      finishDeleteProgress(photo.id, "failed", getErrorMessage(err, t("failed")));
      console.error("Failed to delete photo", err);
      setError(getErrorMessage(err, t("task_photos_delete_failed")));
    } finally {
      setDeletingId(null);
    }
  };


  const renamePhoto = async (photo) => {
    if (!photo?.id || readonly || renamingId) return;
    const currentName = getDisplayName(photo, 0, photoFallbackLabel);
    const nextName = window.prompt(t("task_photos_new_name"), currentName);
    if (nextName === null) return;

    const cleanName = String(nextName || "").trim();
    if (!cleanName) {
      setError(t("task_photos_name_required"));
      return;
    }
    if (cleanName === currentName) return;

    try {
      setRenamingId(photo.id);
      setError("");
      const { data } = await api.patch(`/auth/storage/files/${photo.id}`, { file_name: cleanName });
      setPhotos((prev) => prev.map((item) => (item.id === photo.id ? { ...item, ...data } : item)));
      await emitChanged();
    } catch (err) {
      console.error("Failed to rename photo", err);
      setError(getErrorMessage(err, t("task_photos_rename_failed")));
    } finally {
      setRenamingId(null);
    }
  };

  const goPrev = useCallback(() => {
    setViewerIndex((prev) => {
      if (prev === null || photos.length < 2) return prev;
      return prev <= 0 ? photos.length - 1 : prev - 1;
    });
  }, [photos.length]);

  const goNext = useCallback(() => {
    setViewerIndex((prev) => {
      if (prev === null || photos.length < 2) return prev;
      return prev >= photos.length - 1 ? 0 : prev + 1;
    });
  }, [photos.length]);

  const closeViewer = useCallback(() => setViewerIndex(null), []);

  useEffect(() => {
    if (viewerIndex === null) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") closeViewer();
      if (event.key === "ArrowLeft") goPrev();
      if (event.key === "ArrowRight") goNext();
      if ((event.key === "Delete" || event.key === "Backspace") && currentPhoto && !readonly) deletePhoto(currentPhoto);
    };
    document.addEventListener("keydown", onKey);
    document.body.classList.add("taskPhotosLightboxOpen");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("taskPhotosLightboxOpen");
    };
  }, [closeViewer, currentPhoto, goNext, goPrev, readonly, viewerIndex]);

  const onDragEnter = (event) => {
    if (readonly) return;
    event.preventDefault();
    dropDepthRef.current += 1;
    setIsDragging(true);
  };

  const onDragOver = (event) => {
    if (readonly) return;
    event.preventDefault();
  };

  const onDragLeave = (event) => {
    if (readonly) return;
    event.preventDefault();
    dropDepthRef.current = Math.max(0, dropDepthRef.current - 1);
    if (dropDepthRef.current === 0) setIsDragging(false);
  };

  const onDrop = async (event) => {
    if (readonly) return;
    event.preventDefault();
    dropDepthRef.current = 0;
    setIsDragging(false);
    await uploadFiles(event.dataTransfer.files, uploadCategory);
  };

  const handleTouchStart = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event) => {
    const start = touchStartRef.current;
    const touch = event.changedTouches?.[0];
    touchStartRef.current = null;
    if (!start || !touch) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx > 0) goPrev();
    else goNext();
  };

  const workflowGroups = useMemo(
    () =>
      PHOTO_WORKFLOW_GROUPS.map((group) => ({
        ...group,
        photos: photos.filter(
          (photo) => getPhotoWorkflowGroup(photo) === group.key
        ),
      })),
    [photos]
  );

  const openCategoryPicker = (category = "during") => {
    setUploadCategory(category);
    window.setTimeout(() => inputRef.current?.click(), 0);
  };

  const renderImage = (photo, index, className = "") => {
    const url = previewUrls[photo.id] || "";
    const failed = imageErrors[photo.id] || !url;
    const name = getDisplayName(photo, index, photoFallbackLabel);

    if (failed) {
      return (
        <div className={`taskPhotoImageFallback ${className}`.trim()}>
          <span>{t("task_photos_preview_failed")}</span>
          {photo.web_view_link && (
            <a href={photo.web_view_link} target="_blank" rel="noreferrer">{t("task_photos_open_file")}</a>
          )}
        </div>
      );
    }

    return (
      <img
        className={className}
        src={url}
        alt={name}
        loading={className ? undefined : "lazy"}
        onError={() => setImageErrors((prev) => ({ ...prev, [photo.id]: true }))}
      />
    );
  };

  return (
    <section
      className={`taskPhotosGallery ${isDragging ? "isDragging" : ""}`}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div className="taskPhotosHeader">
        <div className="taskPhotosTitleBlock">
          <span className="taskPhotosIcon" aria-hidden="true">▧</span>
          <div>
            <h3>{displayTitle}</h3>
            <p>{photosMetaLabel}</p>
          </div>
        </div>

        {!readonly && (
          <div className="taskPhotosActions">
            <button
              type="button"
              className="taskPhotosRefresh"
              onClick={() => setCollapsed((value) => !value)}
              title={collapsed ? t("task_photos_expand") : t("task_photos_collapse")}
              aria-expanded={!collapsed}
            >
              {collapsed ? "▾" : "▴"}
            </button>
            <button type="button" className="taskPhotosRefresh" onClick={loadPhotos} disabled={loading || uploading} title={t("task_photos_refresh")}>↻</button>
            <label className={`taskPhotosAddButton ${uploading ? "isDisabled" : ""}`} onClick={() => setUploadCategory("during")}>
              <input
                ref={inputRef}
                type="file"
                accept="image/*,.jpg,.jpeg,.png,.webp,.gif,.bmp,.svg,.heic,.heif,.avif,.tif,.tiff"
                multiple
                disabled={uploading}
                onChange={handleInputChange}
              />
              <span>{uploading ? `${totalUploadProgress}%` : displayAddLabel}</span>
            </label>
          </div>
        )}
      </div>

      {!!uploadQueue.length && (
        <div className={`taskPhotosUploadPanel ${uploading ? "isActive" : ""}`}>
          <div className="taskPhotosUploadTopline">
            <strong>{uploading ? t("task_photos_uploading_drive") : t("task_photos_upload_complete")}</strong>
            <span>{totalUploadProgress}%</span>
          </div>
          <div className="taskPhotosProgressTrack" aria-label={t("task_photos_upload_progress")}>
            <div className="taskPhotosProgressFill" style={{ width: `${totalUploadProgress}%` }} />
          </div>
          <div className="taskPhotosUploadList">
            {uploadQueue.map((item) => (
              <div className={`taskPhotosUploadItem ${item.status}`} key={item.id}>
                <div>
                  <strong title={item.name}>{item.name}</strong>
                  <span>{formatBytes(item.size)} · {item.status === "failed" ? item.error : `${item.progress}%`}</span>
                </div>
                <b>{item.status === "done" ? "✓" : item.status === "failed" ? "!" : "…"}</b>
              </div>
            ))}
          </div>
        </div>
      )}

      {deleteQueue.length > 0 && (
        <div className="taskPhotosProgressPanel delete" aria-live="polite">
          <div className="taskPhotosProgressTop">
            <strong>{bulkDeleting ? t("task_photos_deleting_count", { count: deleteQueue.length }) : t("task_photos_deleting_photo")}</strong>
            <span>{totalDeleteProgress}% → 0%</span>
          </div>
          <div className="taskPhotosProgressTrack delete" aria-label={t("task_photos_delete_progress")}>
            <div className="taskPhotosProgressFill delete" style={{ width: `${totalDeleteProgress}%` }} />
          </div>
          <div className="taskPhotosUploadList compact">
            {deleteQueue.map((item) => (
              <div className={`taskPhotosUploadItem delete ${item.status}`} key={item.id}>
                <div>
                  <strong title={item.name}>{item.name}</strong>
                  <span>{item.status === "failed" ? item.error : item.status === "done" ? t("task_photos_removed") : t("task_photos_progress_left", { progress: item.progress })}</span>
                </div>
                <b>{item.status === "done" ? "✓" : item.status === "failed" ? "!" : "−"}</b>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <div className="taskPhotosError">{error}</div>}

      {isDragging && !readonly && <div className="taskPhotosDropOverlay"><div>{t("drop_photos_here")}</div></div>}

      {!collapsed && selectedCount > 0 && !readonly && (
        <div className="taskPhotosSelectionBar">
          <div>
            <strong>{selectedCount}</strong> {t("chat_selected")}
          </div>
          <div className="taskPhotosSelectionActions">
            <button type="button" onClick={clearSelection} disabled={bulkDeleting}>{t("clear")}</button>
            <button type="button" className="danger" onClick={deleteSelectedPhotos} disabled={bulkDeleting}>
              {bulkDeleting ? t("task_photos_deleting") : t("task_photos_delete_selected_action", { count: selectedCount })}
            </button>
          </div>
        </div>
      )}

      {!collapsed && (loading && !photos.length ? (
        <div className="taskPhotosSkeletonGrid" aria-label={t("task_photos_loading")}>
          {Array.from({ length: 6 }).map((_, index) => (
            <div className="taskPhotosSkeleton" key={index} />
          ))}
        </div>
      ) : photos.length ? (
        workspaceLayout === "workflow" ? (
          <div className="taskPhotosWorkflow">
            {workflowGroups.map((group) => (
              <section
                key={group.key}
                className={`taskPhotosWorkflowColumn taskPhotosWorkflowColumn--${group.key}`}
                onDragOver={(event) => event.preventDefault()}
                onDrop={async (event) => {
                  if (readonly) return;
                  event.preventDefault();
                  event.stopPropagation();
                  dropDepthRef.current = 0;
                  setIsDragging(false);
                  setUploadCategory(group.key);
                  await uploadFiles(event.dataTransfer.files, group.key);
                }}
              >
                <header>
                  <div>
                    <strong>{t(group.label)}</strong>
                    <span>{t(group.hint)}</span>
                  </div>
                  <b>{group.photos.length}</b>
                </header>

                <div className="taskPhotosWorkflowGrid">
                  {group.photos.map((photo) => {
                    const index = photos.findIndex((item) => item.id === photo.id);
                    return (
                      <article
                        className={`taskPhotoCard ${
                          selectedIds.has(photo.id) ? "isSelected" : ""
                        }`}
                        key={photo.id}
                      >
                        {!readonly && (
                          <label
                            className="taskPhotoSelect"
                            title={
                              selectedIds.has(photo.id)
                                ? t("task_photos_unselect")
                                : t("task_photos_select")
                            }
                            onClick={(event) => event.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.has(photo.id)}
                              onChange={() => toggleSelected(photo.id)}
                            />
                            <span aria-hidden="true">✓</span>
                          </label>
                        )}

                        {!readonly && (
                          <button
                            type="button"
                            className="taskPhotoRenameCorner"
                            disabled={renamingId === photo.id || bulkDeleting}
                            onClick={(event) => {
                              event.stopPropagation();
                              renamePhoto(photo);
                            }}
                            title={t("task_photos_rename")}
                          >
                            {renamingId === photo.id ? "…" : "✎"}
                          </button>
                        )}

                        <button
                          type="button"
                          className="taskPhotoPreviewButton"
                          onClick={() => setViewerIndex(index)}
                        >
                          {renderImage(photo, index)}
                          <span className="taskPhotoOpenHint">{t("open")}</span>
                        </button>

                        <div className="taskPhotoMeta">
                          <strong title={getDisplayName(photo, index, photoFallbackLabel)}>
                            {stripPhotoWorkflowPrefix(getDisplayName(photo, index, photoFallbackLabel))}
                          </strong>
                          <span>{formatBytes(photo.size_bytes) || t("task_photos_google_drive")}</span>
                        </div>

                        <div className="taskPhotoQuickActions">
                          <button
                            type="button"
                            className="taskPhotoActionIcon"
                            onClick={() => setViewerIndex(index)}
                            title={t("task_photos_view")}
                          >
                            👁
                          </button>
                          <button
                            type="button"
                            className="taskPhotoActionIcon"
                            onClick={() => downloadPhoto(photo)}
                            disabled={downloadingId === photo.id}
                            title={t("task_photos_download")}
                          >
                            {downloadingId === photo.id ? "…" : "↓"}
                          </button>
                          {!readonly && (
                            <button
                              type="button"
                              className="taskPhotoActionIcon danger"
                              disabled={deletingId === photo.id || bulkDeleting}
                              onClick={() => deletePhoto(photo)}
                              title={t("task_photos_delete")}
                            >
                              {deletingId === photo.id ? "…" : "🗑"}
                            </button>
                          )}
                        </div>
                      </article>
                    );
                  })}

                  {!readonly && (
                    <button
                      type="button"
                      className="taskPhotosWorkflowAdd"
                      disabled={uploading}
                      onClick={() => openCategoryPicker(group.key)}
                    >
                      <span>＋</span>
                      <small>{t("add")}</small>
                    </button>
                  )}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="taskPhotosGrid">
            {photos.map((photo, index) => (
              <article
                className={`taskPhotoCard ${
                  selectedIds.has(photo.id) ? "isSelected" : ""
                }`}
                key={photo.id}
              >
                {!readonly && (
                  <label
                    className="taskPhotoSelect"
                    title={
                      selectedIds.has(photo.id)
                        ? t("task_photos_unselect")
                        : t("task_photos_select")
                    }
                    onClick={(event) => event.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(photo.id)}
                      onChange={() => toggleSelected(photo.id)}
                    />
                    <span aria-hidden="true">✓</span>
                  </label>
                )}

                {!readonly && (
                  <button
                    type="button"
                    className="taskPhotoRenameCorner"
                    disabled={renamingId === photo.id || bulkDeleting}
                    onClick={(event) => {
                      event.stopPropagation();
                      renamePhoto(photo);
                    }}
                    title={t("task_photos_rename")}
                    aria-label={t("task_photos_rename")}
                  >
                    {renamingId === photo.id ? "…" : "✎"}
                  </button>
                )}

                <button
                  type="button"
                  className="taskPhotoPreviewButton"
                  onClick={() => setViewerIndex(index)}
                  title={getDisplayName(photo, index, photoFallbackLabel)}
                >
                  {renderImage(photo, index)}
                  <span className="taskPhotoOpenHint">{t("open")}</span>
                </button>

                <div className="taskPhotoMeta">
                  <strong title={getDisplayName(photo, index, photoFallbackLabel)}>
                    {stripPhotoWorkflowPrefix(getDisplayName(photo, index, photoFallbackLabel))}
                  </strong>
                  <span>{formatBytes(photo.size_bytes) || t("task_photos_google_drive")}</span>
                </div>

                <div className="taskPhotoQuickActions" aria-label={t("task_photos_actions")}>
                  <button
                    type="button"
                    className="taskPhotoActionIcon"
                    onClick={() => setViewerIndex(index)}
                    title={t("task_photos_view")}
                    aria-label={t("task_photos_view")}
                  >
                    👁
                  </button>
                  <button
                            type="button"
                            className="taskPhotoActionIcon"
                            onClick={() => downloadPhoto(photo)}
                            disabled={downloadingId === photo.id}
                            title={t("task_photos_download")}
                            aria-label={t("task_photos_download")}
                          >
                            {downloadingId === photo.id ? "…" : "↓"}
                          </button>
                  {!readonly && (
                    <button
                      type="button"
                      className="taskPhotoActionIcon danger"
                      disabled={deletingId === photo.id || bulkDeleting}
                      onClick={() => deletePhoto(photo)}
                      title={t("task_photos_delete")}
                      aria-label={t("task_photos_delete")}
                    >
                      {deletingId === photo.id ? "…" : "🗑"}
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )
      ) : showEmptyState ? (
        workspaceLayout === "workflow" ? (
          <div className="taskPhotosWorkflow">
            {PHOTO_WORKFLOW_GROUPS.map((group) => (
              <section
                key={group.key}
                className={`taskPhotosWorkflowColumn taskPhotosWorkflowColumn--${group.key}`}
              >
                <header>
                  <div>
                    <strong>{t(group.label)}</strong>
                    <span>{t(group.hint)}</span>
                  </div>
                  <b>0</b>
                </header>
                <div className="taskPhotosWorkflowGrid">
                  {!readonly && (
                    <button
                      type="button"
                      className="taskPhotosWorkflowAdd"
                      disabled={uploading}
                      onClick={() => openCategoryPicker(group.key)}
                    >
                      <span>＋</span>
                      <small>{t("add")}</small>
                    </button>
                  )}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="taskPhotosEmpty">
            <div className="taskPhotosEmptyIcon">⌁</div>
            <strong>{t("task_photos_empty")}</strong>
            <span>
              {t("task_photos_empty_hint")}
            </span>
            {!readonly && (
              <button
                type="button"
                onClick={() => openCategoryPicker("during")}
                disabled={uploading}
              >
                {displayAddLabel}
              </button>
            )}
          </div>
        )
      ) : null)}

      {currentPhoto && (
        <div className="taskPhotoLightbox" role="dialog" aria-modal="true" onClick={closeViewer}>
          <div className="taskPhotoLightboxPanel" onClick={(event) => event.stopPropagation()}>
            <div className="taskPhotoLightboxTopbar">
              <div className="taskPhotoLightboxTitle">
                <strong>{getDisplayName(currentPhoto, viewerIndex || 0, photoFallbackLabel)}</strong>
                <span>{(viewerIndex || 0) + 1} / {photos.length} · {formatBytes(currentPhoto.size_bytes) || t("task_photos_google_drive")}</span>
              </div>

              <div className="taskPhotoLightboxActions">
                {currentPhoto.web_view_link && <a href={currentPhoto.web_view_link} target="_blank" rel="noreferrer">{t("task_photos_open_drive")}</a>}
                <button type="button" disabled={downloadingId === currentPhoto.id} onClick={() => downloadPhoto(currentPhoto)}>
                  {downloadingId === currentPhoto.id ? "…" : t("task_photos_download_action")}
                </button>
                {!readonly && <button type="button" disabled={renamingId === currentPhoto.id} onClick={() => renamePhoto(currentPhoto)}>{renamingId === currentPhoto.id ? t("task_photos_renaming") : t("task_photos_rename")}</button>}
                {!readonly && (
                  <button type="button" className="danger" disabled={deletingId === currentPhoto.id} onClick={() => deletePhoto(currentPhoto)}>
                    {deletingId === currentPhoto.id ? t("task_photos_deleting") : t("delete")}
                  </button>
                )}
                <IconButton onClick={closeViewer} aria-label={t("task_photos_close_viewer")}>×</IconButton>
              </div>
            </div>

            <div className="taskPhotoLightboxStage" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
              {photos.length > 1 && <IconButton className="nav previous" onClick={goPrev} aria-label={t("task_photos_previous")}>‹</IconButton>}
              {renderImage(currentPhoto, viewerIndex || 0, "taskPhotoLightboxImage")}
              {photos.length > 1 && <IconButton className="nav next" onClick={goNext} aria-label={t("task_photos_next")}>›</IconButton>}
            </div>

            {photos.length > 1 && (
              <div className="taskPhotoLightboxStrip">
                {photos.map((photo, index) => (
                  <button type="button" key={photo.id} className={index === viewerIndex ? "active" : ""} onClick={() => setViewerIndex(index)} title={getDisplayName(photo, index, photoFallbackLabel)}>
                    {renderImage(photo, index)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default TaskPhotosGallery;
